import React, { useState, useEffect } from "react";
import toast from "react-hot-toast";
import "./css/LoginFormModal.css"; // Reuse existing modal styles
import InternationalPhoneInput from "../utils/InternationalPhoneInputUtil.js";
import UserAPI from "../services/UserAPI"; // Import UserAPI for account creation and group ID
import SmsConsentAPI from "../services/SmsConsentAPI"; // Import SMS Consent API
import { fetchIpAddress } from "../utils/IPLookupUtil"; // Import IP lookup utility
import { validatePhoneNumber } from "../utils/PhoneValidationUtil"; // Import phone validation utility
import { validatePassword, checkPasswordMatch } from "../utils/PasswordUtil"; // Import password validation utility
import PasswordStrengthMeter from "../utils/PasswordStrengthMeter";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEye,
  faEyeSlash,
  faArrowLeft,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import UserConsentAgreement from "../../legal/UserConsentAgreement";
import { useTranslation } from "../../../i18n/LanguageContext";
import { useEdition } from "../context/EditionContext";

// Industry options for corporate sign-ups. Values are stored as-is on the
// account and surfaced on the cyber-insurance readiness report. Aligned with
// common cyber-insurance underwriting questionnaire categories.
const INDUSTRY_OPTIONS = [
  "Professional Services",
  "Technology & Software",
  "Finance & Insurance",
  "Healthcare",
  "Retail & E-commerce",
  "Manufacturing",
  "Construction",
  "Education",
  "Legal",
  "Real Estate",
  "Hospitality",
  "Transportation & Logistics",
  "Nonprofit",
  "Government & Public Sector",
  "Other",
];

function CreateAccountUser({ onClose, onGoToLogin }) {
  const { t } = useTranslation();
  const { setEditionPreference } = useEdition();
  const [formData, setFormData] = useState({
    accountType: "CONSUMER", // CONSUMER (individual/family) or CORPORATE (business/team)
    companyName: "",
    industry: "",
    employeeCount: "",
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    password: "",
    confirmPassword: "",
    active: true,
  });
  const [errorMessage, setErrorMessage] = useState("");
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [ipAddress, setIpAddress] = useState("");
  const [showVerificationReminder, setShowVerificationReminder] =
    useState(false);
  const [createdUserData, setCreatedUserData] = useState(null);
  const [headerOffset, setHeaderOffset] = useState(0);
  const [isMouseDownOnBackdrop, setIsMouseDownOnBackdrop] = useState(false); // Track mousedown on backdrop
  const [passwordsMatch, setPasswordsMatch] = useState(false);
  const [currentView, setCurrentView] = useState("form"); // "form" or "consent"

  // Fetch the user's IP address when the component mounts
  useEffect(() => {
    const getIpAddress = async () => {
      const ip = await fetchIpAddress();
      setIpAddress(ip);
    };
    getIpAddress();
  }, []);

  // Compute header height (fixed landing header) and store offset for mobile spacing
  useEffect(() => {
    const computeOffset = () => {
      const header = document.querySelector(".landing-header");
      if (header) {
        const h = header.offsetHeight || 0;
        setHeaderOffset(h + 36); // header + breathing room
      } else {
        setHeaderOffset(110);
      }
    };
    computeOffset();
    window.addEventListener("resize", computeOffset);
    window.addEventListener("orientationchange", computeOffset);
    return () => {
      window.removeEventListener("resize", computeOffset);
      window.removeEventListener("orientationchange", computeOffset);
    };
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value, // Remove phone formatting - InternationalPhoneInput handles its own
    }));

    // Real-time password match validation for confirmPassword field
    if (name === "confirmPassword") {
      const matchResult = checkPasswordMatch(formData.password, value);
      setErrors((prevErrors) => ({
        ...prevErrors,
        passwordMismatch: matchResult.hasError,
      }));
      setPasswordsMatch(matchResult.isComplete);
    }

    // Clear error message when user starts typing
    if (errorMessage) {
      setErrorMessage("");
    }

    // Clear field-specific errors when user starts typing
    if (errors[name]) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [name]: false,
      }));
    }
  };

  // Handle terms checkbox change
  const handleTermsChange = (e) => {
    setTermsAccepted(e.target.checked);
  };

  // Select the account edition (individual/family vs business/team)
  const handleAccountTypeChange = (type) => {
    setFormData((prevData) => ({ ...prevData, accountType: type }));
  };

  // Handle verification reminder acknowledgment
  const handleVerificationAcknowledged = () => {
    setShowVerificationReminder(false);
    toast.success(
      `Created a new account for ${createdUserData.firstName} ${createdUserData.lastName}. Please check your email for a verification link.`,
      { duration: 5000 },
    );
    onClose();
  };

  // Check if form is valid for enabling submit button
  const isFormValid = () => {
    const phoneValidation = validatePhoneNumber(formData.phone);
    return (
      formData.firstName.trim() &&
      formData.lastName.trim() &&
      formData.email.trim() &&
      /\S+@\S+\.\S+/.test(formData.email) &&
      formData.phone.trim() &&
      phoneValidation.isValid &&
      formData.password &&
      formData.confirmPassword &&
      formData.password === formData.confirmPassword &&
      termsAccepted
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate form data
    const newErrors = {};

    if (!formData.firstName.trim()) newErrors.firstName = true;
    if (!formData.lastName.trim()) newErrors.lastName = true;
    if (!formData.email.trim() || !/\S+@\S+\.\S+/.test(formData.email))
      newErrors.email = true;

    // Enhanced phone validation
    const phoneValidation = validatePhoneNumber(formData.phone);
    if (!formData.phone.trim() || !phoneValidation.isValid) {
      newErrors.phone = true;
      newErrors.phoneError =
        phoneValidation.error || "Please enter a valid phone number";
    }

    // Validate password strength using NIST-compliant PasswordUtil
    const passwordValidation = validatePassword(formData.password);
    if (!passwordValidation.isValid) {
      newErrors.password = true;
      newErrors.passwordError = passwordValidation.message;
    }

    if (formData.password !== formData.confirmPassword)
      newErrors.passwordMismatch = true;
    if (!termsAccepted) newErrors.termsNotAccepted = true;

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      return;
    } // Create account
    try {
      console.log("Creating account with form data:", formData);

      // Create a payload for account creation
      // Note: No subscription is assigned during account creation.
      // Users will select and subscribe to a service plan after logging in.
      const accountPayload = {
        accountType: formData.accountType,
        companyName:
          formData.accountType === "CORPORATE" ? formData.companyName : "",
        industry: formData.accountType === "CORPORATE" ? formData.industry : "",
        employeeCount:
          formData.accountType === "CORPORATE" ? formData.employeeCount : "",
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        password: formData.password,
        active: formData.active,
        // Add consent information for backend processing
        consentGiven: termsAccepted.toString(),
        consentText:
          "I agree to the Consent Agreement, including consent to receive text messages for training and simulation services.",
        consentVersion: "1.1",
        consentSource: "app",
        ipAddress: ipAddress || "", // Include the IP address if available
      };

      console.log("Account payload:", accountPayload);
      const response = await UserAPI.createAccountOwnerUser(accountPayload); // Create account owner via UserAPI
      console.log("Account creation response:", response);

      // Persist the chosen edition so the post-registration UI immediately uses
      // the right terminology (family vs team) before a subscription exists.
      setEditionPreference(formData.accountType);

      // Save SMS consent record (covered by Consent Agreement)
      if (termsAccepted && formData.phone) {
        try {
          const smsConsentPayload = {
            phoneNumber: formData.phone,
            firstName: formData.firstName,
            lastName: formData.lastName,
            email: formData.email,
            // Required boolean fields for backend validation
            smsConsent: true,
            dataProcessing: true,
            ageConfirmation: true,
            // Optional metadata
            organization: "", // Optional field
            consentTimestamp: new Date().toISOString(),
            userAgent: navigator.userAgent || "",
            ipAddress: ipAddress || "",
          };

          const consentResponse =
            await SmsConsentAPI.createSmsConsent(smsConsentPayload);
          console.log("SMS consent saved successfully:", consentResponse);
        } catch (consentError) {
          console.error("Error saving SMS consent:", consentError);
          // Don't fail account creation if SMS consent fails
        }
      }

      // Store created user data and show verification reminder
      setCreatedUserData({
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        userUuid: response.userUuid, // Store the UUID for verification links
      });

      setShowVerificationReminder(true);
    } catch (error) {
      console.error("Error creating account:", error);

      // Handle specific error cases
      if (error.response && error.response.status === 409) {
        // Email already in use
        const errorMessage =
          error.response.data?.message ||
          "Email address is already in use by another user";
        setErrorMessage(errorMessage);

        // Highlight the email field as having an error
        setErrors((prevErrors) => ({
          ...prevErrors,
          email: true,
        }));
      } else if (error.response && error.response.status === 400) {
        // Bad request - could be validation errors
        const errorMessage =
          error.response.data?.message ||
          error.response.data?.error ||
          "Invalid data provided. Please check your information and try again.";
        setErrorMessage(errorMessage);
        console.error("Bad request details:", error.response.data);
      } else {
        // Generic error handling
        setErrorMessage("Failed to create account. Please try again.");
      }
    }
  };

  const handleBackdropMouseDown = (e) => {
    // Don't handle backdrop clicks if verification reminder is showing
    if (showVerificationReminder) {
      return;
    }

    // Only set flag if clicking directly on the backdrop (not on modal content)
    const className =
      typeof e.target.className === "string"
        ? e.target.className
        : e.target.className?.toString() || "";
    if (className.includes("modal create-account-modal")) {
      setIsMouseDownOnBackdrop(true);
    }
  };

  const handleBackdropMouseUp = (e) => {
    // Don't handle backdrop clicks if verification reminder is showing
    if (showVerificationReminder) {
      setIsMouseDownOnBackdrop(false);
      return;
    }

    // Only close if mouse was pressed down on backdrop AND released on backdrop
    const className =
      typeof e.target.className === "string"
        ? e.target.className
        : e.target.className?.toString() || "";
    if (
      className.includes("modal create-account-modal") &&
      isMouseDownOnBackdrop
    ) {
      onClose(); // Close the modal when complete click (down + up) happens on backdrop
    }
    // Reset the flag regardless
    setIsMouseDownOnBackdrop(false);
  };

  return (
    <div
      className="modal create-account-modal"
      onMouseDown={handleBackdropMouseDown}
      onMouseUp={handleBackdropMouseUp}
    >
      {/* Create Account Form View */}
      {currentView === "form" && (
        <div className="modal-content">
          <button
            className="close"
            onClick={() => {
              onClose();
              onGoToLogin();
            }}
            aria-label="Close"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
          <h2>Create an Account</h2>
          <form onSubmit={handleSubmit}>
            <div className="account-type-selector" style={{ marginBottom: 20 }}>
              <div
                className="account-type-options"
                style={{ display: "flex", gap: 10 }}
              >
                {[
                  {
                    type: "CONSUMER",
                    title: t("register.accountType.individual"),
                  },
                  {
                    type: "CORPORATE",
                    title: t("register.accountType.business"),
                  },
                ].map((opt) => {
                  const selected = formData.accountType === opt.type;
                  return (
                    <button
                      key={opt.type}
                      type="button"
                      onClick={() => handleAccountTypeChange(opt.type)}
                      aria-pressed={selected}
                      style={{
                        flex: 1,
                        textAlign: "center",
                        padding: "10px 12px",
                        borderRadius: 8,
                        border: selected
                          ? "2px solid #1976d2"
                          : "1px solid #ddd",
                        background: selected ? "#e8f1fc" : "#f8f8f8",
                        fontWeight: 600,
                        cursor: "pointer",
                      }}
                    >
                      {opt.title}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="account-details-section">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="firstName">First Name</label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    style={{
                      borderColor: errors.firstName ? "lightcoral" : "#ddd",
                    }}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="lastName">Last Name</label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    style={{
                      borderColor: errors.lastName ? "lightcoral" : "#ddd",
                    }}
                  />
                </div>
              </div>
              {formData.accountType === "CORPORATE" && (
                <div className="form-group">
                  <label htmlFor="companyName">
                    {t("register.companyName")}
                  </label>
                  <input
                    type="text"
                    id="companyName"
                    name="companyName"
                    placeholder={t("register.companyNamePlaceholder")}
                    value={formData.companyName}
                    onChange={handleChange}
                    style={{ borderColor: "#ddd" }}
                  />
                </div>
              )}
              {formData.accountType === "CORPORATE" && (
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="industry">
                      {t("register.companyMeta.industry")}
                    </label>
                    <select
                      id="industry"
                      name="industry"
                      value={formData.industry}
                      onChange={handleChange}
                      style={{
                        borderColor: "#ddd",
                        width: "100%",
                        padding: "10px",
                        borderRadius: 6,
                        background: "#f1eded",
                      }}
                    >
                      <option value="">
                        {t("register.companyMeta.industryPlaceholder")}
                      </option>
                      {INDUSTRY_OPTIONS.map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label htmlFor="employeeCount">
                      {t("register.companyMeta.employeeCount")}
                    </label>
                    <input
                      type="number"
                      id="employeeCount"
                      name="employeeCount"
                      min="1"
                      step="1"
                      inputMode="numeric"
                      placeholder={t(
                        "register.companyMeta.employeeCountPlaceholder",
                      )}
                      value={formData.employeeCount}
                      onChange={handleChange}
                      style={{ borderColor: "#ddd" }}
                    />
                  </div>
                </div>
              )}
              <div className="form-group">
                <label htmlFor="email">Email</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  placeholder="e.g., example@domain.com"
                  value={formData.email}
                  onChange={handleChange}
                  style={{
                    borderColor: errors.email ? "lightcoral" : "#ddd",
                    backgroundColor:
                      formData.email &&
                      formData.email.trim() &&
                      !/\S+@\S+\.\S+/.test(formData.email)
                        ? "#ffebee"
                        : "#f1eded",
                  }}
                />
                {formData.accountType === "CORPORATE" && (
                  <small
                    style={{
                      display: "block",
                      marginTop: 4,
                      color: "#666",
                      fontSize: "0.78rem",
                      lineHeight: 1.3,
                    }}
                  >
                    {t("register.companyAdminEmailHelp")}
                  </small>
                )}
                {errors.email && (
                  <small style={{ color: "lightcoral" }}>
                    Please enter a valid email address.
                  </small>
                )}
              </div>
              <div className="form-group">
                <label htmlFor="phone">Phone Number</label>
                <InternationalPhoneInput
                  value={formData.phone}
                  onChange={(e164Value) => {
                    setFormData((prev) => ({
                      ...prev,
                      phone: e164Value,
                    }));

                    // Clear phone error when valid phone number is entered
                    const phoneValidation = validatePhoneNumber(e164Value);
                    if (phoneValidation.isValid) {
                      setErrors((prevErrors) => ({
                        ...prevErrors,
                        phone: false,
                        phoneError: null,
                      }));
                    }

                    // Clear general error message when user starts typing
                    if (errorMessage) {
                      setErrorMessage("");
                    }
                  }}
                  error={
                    errors.phone
                      ? errors.phoneError || "Please enter a valid phone number"
                      : ""
                  }
                  isValid={
                    formData.phone
                      ? validatePhoneNumber(formData.phone).isValid
                      : true
                  }
                />
              </div>
              <div className="form-group">
                <label htmlFor="password">Password</label>
                <div className="password-container">
                  <input
                    type={showPassword ? "text" : "password"}
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    minLength={12}
                    maxLength={128}
                    style={{
                      borderColor:
                        errors.password || errors.passwordMismatch
                          ? "lightcoral"
                          : "#ddd",
                    }}
                  />
                  <span
                    className="eye-icon"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                  </span>
                </div>
                <PasswordStrengthMeter password={formData.password} />
                {errors.passwordError && (
                  <small
                    style={{
                      color: "lightcoral",
                      display: "block",
                      marginTop: "4px",
                      fontSize: "0.85rem",
                    }}
                  >
                    {errors.passwordError}
                  </small>
                )}
              </div>
              <div className="form-group">
                <label htmlFor="confirmPassword">Confirm Password</label>
                <div className="password-container">
                  <input
                    type={showPassword ? "text" : "password"}
                    id="confirmPassword"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    minLength={12}
                    maxLength={128}
                    style={{
                      borderColor: errors.passwordMismatch
                        ? "lightcoral"
                        : "#ddd",
                      backgroundColor:
                        formData.confirmPassword &&
                        formData.password &&
                        formData.password !== formData.confirmPassword
                          ? "#ffebee"
                          : "#f1eded",
                    }}
                  />
                  <span
                    className="eye-icon"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                  </span>
                </div>
                {errors.passwordMismatch && formData.confirmPassword && (
                  <small
                    style={{
                      color: "lightcoral",
                      display: "block",
                      marginTop: "4px",
                    }}
                  >
                    Passwords do not match
                  </small>
                )}
                {passwordsMatch && (
                  <small
                    style={{
                      color: "#28a745",
                      display: "block",
                      marginTop: "4px",
                      fontWeight: "600",
                    }}
                  >
                    ✓ Passwords match
                  </small>
                )}
              </div>
              <div className="form-group terms-consent">
                <input
                  type="checkbox"
                  id="termsConsent"
                  name="termsConsent"
                  className="checkbox"
                  checked={termsAccepted}
                  onChange={handleTermsChange}
                  style={{
                    borderColor: errors.termsNotAccepted
                      ? "lightcoral"
                      : "#ddd",
                  }}
                />
                <label htmlFor="termsConsent" className="checkbox-label">
                  &nbsp; I agree to the{" "}
                  <button
                    type="button"
                    className="link-button-inline"
                    onClick={() => setCurrentView("consent")}
                  >
                    Consent Agreement
                  </button>
                </label>
                {errors.termsNotAccepted && (
                  <small style={{ color: "lightcoral", display: "block" }}>
                    You must accept the Consent Agreement to continue.
                  </small>
                )}
              </div>
            </div>
            {errorMessage && <p className="error-message">{errorMessage}</p>}
            <button
              type="submit"
              className="login-button"
              disabled={!isFormValid()}
              style={{
                opacity: isFormValid() ? 1 : 0.8,
                cursor: isFormValid() ? "pointer" : "not-allowed",
                backgroundColor: isFormValid() ? "" : "#ccc",
                color: isFormValid() ? "" : "#555",
              }}
            >
              Create Account
            </button>
          </form>
          <div className="footer-links">
            Already have an account? &nbsp;
            <button
              type="button"
              className="link-button"
              onClick={() => {
                onClose();
                onGoToLogin();
              }}
            >
              <strong>Go to Login</strong>
            </button>
          </div>
        </div>
      )}

      {/* Consent Agreement View */}
      {currentView === "consent" && (
        <div className="login-modal-content legal-view">
          <button
            className="close"
            onClick={() => setCurrentView("form")}
            aria-label="Close"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
          <button
            className="back-to-login-button"
            onClick={() => setCurrentView("form")}
          >
            <FontAwesomeIcon icon={faArrowLeft} /> Back to Create Account
          </button>
          <div className="legal-content-wrapper">
            <UserConsentAgreement />
          </div>
        </div>
      )}

      {/* Verification Reminder Modal */}
      {showVerificationReminder && createdUserData && (
        <div
          className="modal verification-reminder-modal"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="modal-content">
            <h2>Account Created Successfully!</h2>
            <div className="verification-reminder-content">
              <p>
                <strong>
                  Welcome, {createdUserData.firstName}{" "}
                  {createdUserData.lastName}!
                </strong>
              </p>
              <p>Your account has been created successfully.</p>
              <p>
                Please complete the following verification steps to activate
                your account:
              </p>
              <div className="verification-steps">
                <h3>Verification Steps:</h3>
                <ol>
                  <li>
                    <strong>Email Verification:</strong>
                    <br />
                    Check your email inbox at{" "}
                    <strong>{createdUserData.email}</strong> and click the
                    verification link
                  </li>
                  <li>
                    <strong>Phone Verification:</strong>
                    <br />
                    When you log in for the first time, an SMS with a
                    verification code will be sent to{" "}
                    <strong>{createdUserData.phone}</strong>. You'll be prompted
                    then to enter the code to verify your phone number.
                  </li>
                  <li>
                    Once both verifications are complete, your account will be
                    fully activated
                  </li>
                </ol>
              </div>

              <div className="verification-note">
                <p>
                  <em>
                    Note: Please check your spam/junk folder if you don't see
                    the verification email. If you don't receive the SMS, you
                    can request a new code when you log in.
                  </em>
                </p>
              </div>
              <button
                className="login-button verification-acknowledge-btn secondary"
                onClick={handleVerificationAcknowledged}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CreateAccountUser;
